import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Brand } from "@/components/Brand";
import { CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * InviteSetup — public /invite/:token page.
 *
 * The invitee lands here from an admin-approved access request email. We
 * fetch a preview (email/name/expires_at) so the page can greet them; then
 * they choose their own password. On success the backend returns a JWT and
 * we drop them straight into /dashboard.
 */
export default function InviteSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState({ loading: true, preview: null, error: null });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/invite/${encodeURIComponent(token)}`);
        if (alive) setState({ loading: false, preview: r.data, error: null });
      } catch (e) {
        const detail = e?.response?.data?.detail || "This invite link is not valid.";
        if (alive) setState({ loading: false, preview: null, error: detail });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    if (password.length < 12) { setErrMsg("Password must be at least 12 characters."); return; }
    if (password !== confirm) { setErrMsg("Passwords do not match."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/invite/${encodeURIComponent(token)}/complete`, { password });
      const { token: jwt, user } = r.data;
      if (jwt) localStorage.setItem("sahoi_token", jwt);
      if (user) setUser(user);
      navigate("/dashboard", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not complete setup. Please try again.";
      setErrMsg(detail);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAF9] text-slate-900" data-testid="invite-setup-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-3"><Brand variant="dark" size={18} /></div>
      </header>
      <main className="max-w-md mx-auto px-4 py-12">
        {state.loading && <div className="text-sm text-slate-500" data-testid="invite-loading">Checking invite…</div>}
        {state.error && (
          <div className="p-5 rounded-lg border border-amber-300 bg-amber-50" data-testid="invite-error">
            <AlertTriangle className="w-5 h-5 text-amber-700 mb-2" />
            <div className="text-sm font-semibold text-amber-900">This invite link is not valid</div>
            <div className="text-xs text-amber-800 mt-1">{state.error}</div>
            <div className="text-xs text-amber-800 mt-3">Reply to your invite email and an admin will issue a new link.</div>
          </div>
        )}
        {state.preview && (
          <form onSubmit={submit} className="p-6 rounded-lg border border-slate-200 bg-white space-y-4" data-testid="invite-form">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Set your password</div>
              <h1 className="text-2xl font-bold mt-1 tracking-[-0.01em]">Welcome to Throughline</h1>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                You&apos;re about to set up your account for{" "}
                <span className="font-semibold text-slate-900">{state.preview.email}</span>.
                Choose a password (12 characters minimum). This link works one time.
              </p>
            </div>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">New password</span>
              <input
                type="password" required minLength={12} value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2.5 text-sm bg-white"
                data-testid="invite-password"
              />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Confirm password</span>
              <input
                type="password" required minLength={12} value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2.5 text-sm bg-white"
                data-testid="invite-confirm"
              />
            </label>
            {errMsg && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="invite-err">{errMsg}</div>}
            <button type="submit" disabled={busy}
                    className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold rounded-md py-3 min-h-[48px]"
                    data-testid="invite-submit">
              {busy ? "Setting up…" : "Set password and sign in"}
            </button>
            <div className="text-[11px] text-slate-500 flex gap-1.5 items-start pt-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
              <span>Your password is only stored as a hash. Throughline never sees it.</span>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
